/**
 * Zentrale Helper fuer HERO-Live-Suche aus Modals.
 *
 * Vorher: jedes Modal hat einen eigenen fetch zu /admin/{emails,sipgate,calls}
 * /api/search-{hero-contacts,projects} gebaut. Jetzt: zentral via /api/v1/...
 *
 * Die Endpoints liefern v1-Schema (`{items, total}`) — die Modal-Components
 * koennen `data.items` direkt rendern, kein Adapter pro Mode mehr noetig.
 */

export type HeroContactHit = {
  id: number
  first_name: string
  last_name: string
  company_name: string
  email: string
  phone: string
  address: string
}

/**
 * Anzeigename eines HERO-Kontakt-Treffers.
 *
 * 🔴 Reine Firmenkontakte haben in HERO WEDER Vor- NOCH Nachnamen — dort steht
 * nur `company_name`. „Rhion Versicherung AG" (Kontaktnummer 7782579) kam
 * deshalb als leere Zeile in der Trefferliste an, und fuer die Melderin sah es
 * aus, als faende die Suche den Kontakt gar nicht (#52, 26.08.2026). HERO
 * liefert ihn sehr wohl — nur hatte niemand einen Namen zum Anzeigen.
 *
 * Bewusst hier und nicht in den Modals: es gibt sechs Trefferlisten ueber
 * dieselbe Suche. Als Regel in jedem einzelnen Dialog waere sie beim naechsten
 * neuen Dialog wieder vergessen (dieselbe Lehre wie beim Termin-Titel, #41).
 */
export function kontaktName(c: {
  id?: number
  first_name?: string | null
  last_name?: string | null
  company_name?: string | null
}): string {
  return (
    [c.first_name, c.last_name].filter(Boolean).join(" ").trim() ||
    (c.company_name || "").trim() ||
    (c.id ? `Kontakt #${c.id}` : "(ohne Name)")
  )
}

/**
 * Setzt einen gewaehlten Kontakt ins Empfaengerfeld. Der Suchbegriff, den der
 * Nutzer gerade tippt (letztes Stueck nach dem Komma, ohne „@"), wird dabei
 * ERSETZT — sonst blieb er als eigener Empfaenger stehen („env, "Enver …"
 * <…>") und IONOS lehnte die ganze Mail mit 554 ab (Mail #60290, 22.10.).
 */
export function empfaengerMitKontakt(value: string, addr: string): string {
  const teile = value.split(",")
  const letztes = (teile[teile.length - 1] || "").trim()
  if (!letztes.includes("@")) teile.pop()
  const rest = teile.map((t) => t.trim()).filter(Boolean)
  return [...rest, addr].join(", ")
}

/**
 * Firma als Zusatzzeile — aber nur, wenn sie nicht schon der Anzeigename ist.
 * Sonst stuende „Rhion Versicherung AG · Rhion Versicherung AG" da.
 */
export function kontaktFirmaZusatz(c: {
  first_name?: string | null
  last_name?: string | null
  company_name?: string | null
}): string {
  const person = [c.first_name, c.last_name].filter(Boolean).join(" ").trim()
  return person ? (c.company_name || "").trim() : ""
}

export type HeroProjectHit = {
  id: number
  name: string
  code: string | null
  contact_name: string
  status: string
}

const buildUrl = (path: string, params: Record<string, string>) => {
  const qs = new URLSearchParams(params).toString()
  return `${path}?${qs}`
}

/**
 * 🔴 Wirft bei einem Fehler, statt `[]` zurueckzugeben. Vorher war ein
 * Serverfehler im Dialog nicht von „nichts gefunden" zu unterscheiden — der
 * Nutzer legte den Kontakt dann ein zweites Mal an (#52). Jeder Aufrufer
 * faengt den Fehler und sagt, dass die SUCHE nicht ging.
 */
export async function searchHeroContacts(
  tenantSlug: string,
  query: string,
): Promise<HeroContactHit[]> {
  const q = query.trim()
  if (!tenantSlug || !q) return []
  const res = await fetch(
    buildUrl("/api/v1/contacts/search", { tenant: tenantSlug, q }),
    { credentials: "include" },
  )
  if (!res.ok) {
    const d = await res.json().catch(() => ({}))
    throw new Error(d.detail || d.error || `Kontaktsuche fehlgeschlagen (HTTP ${res.status})`)
  }
  const d = await res.json()
  return Array.isArray(d.items) ? (d.items as HeroContactHit[]) : []
}

export async function searchHeroProjects(
  tenantSlug: string,
  query: string,
): Promise<HeroProjectHit[]> {
  const q = query.trim()
  if (!tenantSlug || !q) return []
  try {
    const res = await fetch(
      buildUrl("/api/v1/projects/search", { tenant: tenantSlug, q }),
      { credentials: "include" },
    )
    if (!res.ok) return []
    const d = await res.json()
    return Array.isArray(d.items) ? (d.items as HeroProjectHit[]) : []
  } catch {
    return []
  }
}
